import { keydownHandler } from "prosemirror-keymap";
import {
    Command,
    EditorState,
    Plugin,
    TextSelection,
    Transaction,
} from "prosemirror-state";
import { EditorView } from "prosemirror-view";

import type { MenuFunction } from "@/wc/text-editor/lib/core/menu";
import { MenuItem } from "@/wc/text-editor/lib/core/menu";
import {
    addPopup,
    closeAllPopups,
    closePopup,
    getPopupContents,
    getPopupSpec,
} from "@/wc/text-editor/lib/core/popups";

import { LinkedEntity } from ".";
import { MatchInput } from ".";
import { InsertEntityMenu } from "./component/insert-menu";
import { LinkedEntitiesManager } from "./manager";

/**
 * Keybindings for the popup menu.
 */
const createMenuKeybindings = (popup: {
    key: string;
}): Record<string, Command> => {
    return {
        up: (state) => {
            const menu = getPopupContents<InsertEntityMenu>(state, popup.key);
            if (menu) {
                menu.up();
                return true;
            }
            return false;
        },
        down: (state) => {
            const menu = getPopupContents<InsertEntityMenu>(state, popup.key);
            if (menu) {
                menu.down();
                return true;
            }
            return false;
        },
        select: (state) => {
            const menu = getPopupContents<InsertEntityMenu>(state, popup.key);
            if (menu) {
                menu.insertSelection();
                return true;
            }
            return false;
        },
        close: (state, dispatch) => {
            if (
                dispatch &&
                getPopupContents<InsertEntityMenu>(state, popup.key)
            ) {
                dispatch(closePopup(state.tr, popup.key));
                return true;
            }
            return false;
        },
    };
};

/**
 * Replace the matched text and insert the upgraded link.
 */
const insertEntity =
    (view: EditorView, dispatch: (tr: Transaction) => void, nodeType: string) =>
    (
        start: number,
        end: number,
        entity: Pick<LinkedEntity, "id" | "name" | "href" | "type">,
        closePopupKey: string | null = null
    ) => {
        const { id: _, ...otherAttrs } = entity;
        const entityNode = view.state.schema.node(nodeType as string, {
            ...otherAttrs,
            entityId: entity.id,
        });

        // Create a new transaction based on the current state
        const tr = view.state.tr.replaceWith(start, end, entityNode);
        if (closePopupKey) {
            tr.setMeta("closePopup", closePopupKey);
        }

        dispatch(tr);
        view.focus();
    };

/**
 * Open the input menu.
 */
const openInsertPopup = (
    state: EditorState,
    input: MatchInput,
    manager: LinkedEntitiesManager,
    nodeType: string,
    popup: { key: string },
    tr?: Transaction
) => {
    return addPopup(tr || state.tr, popup.key, {
        create: (view) =>
            new InsertEntityMenu(
                input,
                insertEntity(view, view.dispatch, nodeType),
                manager
            ),
        startPos: input.start,
        closeOnMouseLeave: false,
    });
};

/**
 * Plugin to insert entities.
 */
export const createInsertPlugin = (
    nodeType: string,
    popup: { key: string },
    getManager: () => LinkedEntitiesManager,
    inputRegExp: RegExp
) => {
    const MenuKeybindings = createMenuKeybindings(popup);
    return new Plugin({
        appendTransaction(transactions, _oldState, newState) {
            if (!inputRegExp) {
                return;
            }
            for (const transaction of transactions) {
                if (!transaction.docChanged) {
                    continue;
                }

                const { $cursor } = newState.selection as TextSelection;
                if (!$cursor) {
                    return;
                }
                const match =
                    $cursor.nodeBefore?.textContent.match(inputRegExp);
                if (!match || $cursor.nodeBefore?.attrs.type === nodeType) {
                    if (getPopupSpec(newState, popup.key)) {
                        return closePopup(newState.tr, popup.key);
                    }
                    continue;
                }

                const popupOpen = Boolean(getPopupSpec(newState, popup.key));
                const input = {
                    match,
                    start: $cursor.pos - match[0].length,
                    end: $cursor.pos,
                } as MatchInput;

                if (!popupOpen) {
                    const entitiesManager = getManager();
                    let transaction = closeAllPopups(newState.tr, newState);
                    return openInsertPopup(
                        newState,
                        input,
                        entitiesManager,
                        nodeType,
                        popup,
                        transaction
                    );
                } else {
                    const popupSpec = getPopupSpec(newState, popup.key);
                    if (popupSpec) {
                        popupSpec.startPos = input.start;
                        popupSpec.endPos = input.end;
                    }

                    const menu = getPopupContents<InsertEntityMenu>(
                        newState,
                        popup.key
                    );
                    if (menu) {
                        menu.input = input;
                        return newState.tr;
                    }
                }
            }
        },
        props: {
            handleKeyDown: keydownHandler({
                ArrowUp: MenuKeybindings.up,
                ArrowDown: MenuKeybindings.down,
                Enter: MenuKeybindings.select,
                Tab: MenuKeybindings.select,
                Escape: MenuKeybindings.close,
            }),
            handleClick: (view) => {
                view.dispatch(closePopup(view.state.tr, popup.key));
            },
        },
    });
};

/**
 * Human type tags shown next to entity items when the inline insert menu is
 * in its flattened, query-filtered mode.
 */
const FLAT_GROUP_LABELS: Record<string, string> = {
    domain: "Domain",
    product: "Product",
    process: "Process",
    business_entity: "Domain object",
    domain_term: "Term",
    role: "Role",
};

export const createMenu = (
    nodeType: string,
    menu: Omit<MenuItem, "type" | "getItems" | "items" | "name">,
    popup: {
        key: string;
    },
    getManager: () => LinkedEntitiesManager
) => {
    if (!menu || !nodeType) {
        return;
    }

    return (currentMenu: MenuFunction): MenuFunction => {
        return (view) => {
            return [
                ...currentMenu(view),
                {
                    name: nodeType,
                    type: "dropdown",
                    showLabel: false,
                    getItems: async () => {
                        const entitiesManager = getManager();
                        await entitiesManager.ready;
                        if (entitiesManager.entities?.length) {
                            return entitiesManager.entities
                                .toSorted((a, b) => {
                                    const groupCompare = (
                                        a.group || ""
                                    ).localeCompare(b.group || "");
                                    return (
                                        groupCompare ||
                                        (a.order_index ?? Infinity) -
                                            (b.order_index ?? Infinity) ||
                                        a.name.localeCompare(b.name)
                                    );
                                })
                                .map((entity) => {
                                    const linkedProducts =
                                        nodeType === "domain"
                                            ? (
                                                  entity.data as {
                                                      linkedProducts?: Array<
                                                          Pick<
                                                              LinkedEntity,
                                                              | "id"
                                                              | "name"
                                                              | "href"
                                                              | "type"
                                                          > & {
                                                              linkedProcesses?: Array<
                                                                  Pick<
                                                                      LinkedEntity,
                                                                      | "id"
                                                                      | "name"
                                                                      | "href"
                                                                      | "type"
                                                                  >
                                                              >;
                                                          }
                                                      >;
                                                  }
                                              )?.linkedProducts || []
                                            : [];

                                    if (
                                        nodeType === "domain" &&
                                        linkedProducts.length
                                    ) {
                                        return {
                                            type: "dropdown",
                                            label: entity.name,
                                            name: entity.id,
                                            flatGroup: FLAT_GROUP_LABELS.domain,
                                            icon: {
                                                name: "pi-cloud",
                                                library: "primeicons",
                                            },
                                            run: (state, dispatch, view) => {
                                                const { from, to } =
                                                    state.selection as TextSelection;
                                                insertEntity(
                                                    view,
                                                    dispatch,
                                                    "domain"
                                                )(from, to, entity, popup.key);
                                            },
                                            items: linkedProducts.map(
                                                (product) => ({
                                                    type: "dropdown",
                                                    label: product.name,
                                                    name: product.id,
                                                    flatGroup:
                                                        FLAT_GROUP_LABELS.product,
                                                    icon: {
                                                        name: "pi-box",
                                                        library: "primeicons",
                                                    },
                                                    run: (
                                                        state,
                                                        dispatch,
                                                        view
                                                    ) => {
                                                        const { from, to } =
                                                            state.selection as TextSelection;
                                                        insertEntity(
                                                            view,
                                                            dispatch,
                                                            "product"
                                                        )(
                                                            from,
                                                            to,
                                                            product,
                                                            popup.key
                                                        );
                                                    },
                                                    items: (
                                                        product.linkedProcesses ||
                                                        []
                                                    ).map((process) => ({
                                                        type: "toggle",
                                                        label: process.name,
                                                        name: process.id,
                                                        flatGroup:
                                                            FLAT_GROUP_LABELS.process,
                                                        icon: {
                                                            name: "pi-share-alt",
                                                            library:
                                                                "primeicons",
                                                        },
                                                        run: (
                                                            state,
                                                            dispatch,
                                                            view
                                                        ) => {
                                                            const { from, to } =
                                                                state.selection as TextSelection;
                                                            insertEntity(
                                                                view,
                                                                dispatch,
                                                                "process"
                                                            )(
                                                                from,
                                                                to,
                                                                process,
                                                                popup.key
                                                            );
                                                        },
                                                    })),
                                                })
                                            ),
                                        };
                                    }

                                    return {
                                        type: "toggle",
                                        label: entity.name,
                                        name: entity.id,
                                        flatGroup:
                                            FLAT_GROUP_LABELS[nodeType] ??
                                            nodeType,
                                        icon:
                                            nodeType === "domain"
                                                ? {
                                                      name: "pi-cloud",
                                                      library: "primeicons",
                                                  }
                                                : undefined,
                                        run: (state, dispatch, view) => {
                                            const { from, to } =
                                                state.selection as TextSelection;
                                            insertEntity(
                                                view,
                                                dispatch,
                                                nodeType
                                            )(from, to, entity, popup.key);
                                        },
                                    };
                                }) as MenuItem["items"];
                        } else {
                            return [
                                {
                                    name: "no-entities",
                                    type: "toggle",
                                    disabled: () => true,
                                    label: "Nothing found.",
                                },
                            ] as MenuItem["items"];
                        }
                    },
                    ...menu,
                } as MenuItem,
            ];
        };
    };
};
